import {
  Badge,
  Button,
  buttonVariants,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  EmptyState,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from "@forge/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Copy, Radio, RefreshCw, Send } from "lucide-react";
import { useState } from "react";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";
import { formatDateTime } from "../../lib/format";

export const Route = createFileRoute("/_app/connections_/outbound")({
  component: OutboundPage,
});

const EVENT_OPTIONS = [
  {
    id: "order.paid" as const,
    title: "Pedido pago",
    description: "Quando o pedido entra em pago e o estoque já descontou.",
  },
  {
    id: "stock.changed" as const,
    title: "Estoque mudou",
    description: "Venda, ajuste, recebimento, inventário ou transferência.",
  },
  {
    id: "claim.opened" as const,
    title: "Reclamação aberta",
    description: "Primeira vez que o Bridge vê aquela reclamação.",
  },
];

type OutboundEvent = (typeof EVENT_OPTIONS)[number]["id"];

function apiErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === "object" && "value" in error) {
    const value = (error as { value: unknown }).value;
    if (
      value &&
      typeof value === "object" &&
      "error" in value &&
      typeof (value as { error: unknown }).error === "string"
    ) {
      return (value as { error: string }).error;
    }
  }
  return fallback;
}

async function copyText(value: string) {
  await navigator.clipboard.writeText(value);
}

function eventLabel(event: string) {
  return EVENT_OPTIONS.find((row) => row.id === event)?.title ?? event;
}

function statusVariant(status: string) {
  if (status === "delivered") return "success" as const;
  if (status === "failed") return "danger" as const;
  if (status === "skipped") return "neutral" as const;
  return "warning" as const;
}

function statusLabel(status: string) {
  if (status === "delivered") return "Entregue";
  if (status === "failed") return "Falhou";
  if (status === "skipped") return "Ignorado";
  return "Pendente";
}

function OutboundPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [revealedReadToken, setRevealedReadToken] = useState<string | null>(null);
  const [name, setName] = useState("Webhook");
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<OutboundEvent[]>([
    "order.paid",
    "stock.changed",
    "claim.opened",
  ]);

  const list = useQuery({
    queryKey: ["outbound"],
    queryFn: async () => unwrap(await api.integrations.outbound.get()),
    refetchInterval: 8_000,
  });

  const create = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.integrations.outbound.post({
          name: name.trim() || "Webhook",
          url: url.trim(),
          events,
        }),
      ),
    onSuccess: (endpoint) => {
      setRevealedSecret(endpoint.secret);
      setUrl("");
      void queryClient.invalidateQueries({ queryKey: ["outbound"] });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível criar o webhook",
        description: apiErrorMessage(error, "Confira a URL e tente de novo."),
      });
    },
  });

  const endpoints = list.data?.endpoints ?? [];
  const deliveries = list.data?.deliveries ?? [];

  function toggleEvent(id: OutboundEvent) {
    setEvents((current) =>
      current.includes(id)
        ? current.filter((event) => event !== id)
        : [...current, id],
    );
  }

  return (
    <>
      <PageHeader
        parent={
          <Link
            to="/connections"
            className="text-2xs font-medium text-ink-muted transition-colors duration-100 hover:text-ink"
          >
            Conexões
          </Link>
        }
        title="Saída para o ERP"
        description="Webhooks assinados e uma API só de leitura — o mesmo modelo de token da entrada."
        actions={
          <Link
            to="/connections"
            className={buttonVariants({ variant: "secondary", size: "sm" })}
          >
            <ArrowLeft />
            Conexões
          </Link>
        }
      />

      {list.isLoading ? (
        <Card>
          <CardContent className="py-16 text-center text-2xs text-ink-muted">
            Carregando webhooks…
          </CardContent>
        </Card>
      ) : endpoints.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Radio />}
            title="Nenhuma saída ainda"
            description="Cole a URL que vai receber os eventos, escolha o que assinar e guarde o segredo HMAC. Qualquer sistema que valide a assinatura serve."
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {endpoints.map((endpoint, index) => (
            <EndpointCard
              key={endpoint.id}
              endpoint={endpoint}
              delay={Math.min(index, 6) * 40}
              onReveal={setRevealedSecret}
            />
          ))}
        </div>
      )}

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Novo webhook</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Field>
                <FieldLabel>Nome</FieldLabel>
                <Input
                  value={name}
                  maxLength={40}
                  onChange={(event) => setName(event.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel>URL</FieldLabel>
                <Input
                  required
                  type="url"
                  placeholder="https://seusistema.com.br/webhooks/forge"
                  value={url}
                  onChange={(event) => setUrl(event.target.value)}
                />
                <FieldDescription>
                  POST JSON com HMAC-SHA256 em <code>X-Forge-Signature</code>.
                </FieldDescription>
              </Field>
            </div>
            <ul className="divide-y divide-line rounded-control border border-line">
              {EVENT_OPTIONS.map((row) => (
                <li key={row.id}>
                  <label className="flex cursor-pointer items-start justify-between gap-4 px-3 py-2.5 transition-colors duration-150 ease-swift [@media(hover:hover)_and_(pointer:fine)]:hover:bg-surface-sunken">
                    <span className="min-w-0">
                      <span className="block text-[0.8125rem] font-medium text-ink">
                        {row.title}
                      </span>
                      <span className="mt-0.5 block text-2xs text-ink-muted">
                        {row.description}
                      </span>
                    </span>
                    <Switch
                      className="mt-0.5"
                      checked={events.includes(row.id)}
                      onCheckedChange={() => toggleEvent(row.id)}
                    />
                  </label>
                </li>
              ))}
            </ul>
            <Button
              type="submit"
              className="self-start"
              loading={create.isPending}
              disabled={events.length === 0}
            >
              Criar webhook
            </Button>
          </form>
        </CardContent>
      </Card>

      <ReadApiSection onReveal={setRevealedReadToken} />

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Entregas</CardTitle>
          {deliveries.length > 0 ? (
            <Badge variant="neutral">{deliveries.length}</Badge>
          ) : null}
        </CardHeader>
        {deliveries.length === 0 ? (
          <CardContent className="text-2xs text-ink-muted">
            Ainda não saiu nenhum evento. Um teste ou o próximo pedido pago
            aparece aqui.
          </CardContent>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Quando</TableHead>
                <TableHead>Evento</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Tentativas</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deliveries.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="text-2xs text-ink-muted">
                    {formatDateTime(row.createdAt)}
                  </TableCell>
                  <TableCell>{eventLabel(row.event)}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(row.status)} dot>
                      {statusLabel(row.status)}
                      {row.lastStatusCode ? ` · ${row.lastStatusCode}` : ""}
                    </Badge>
                    {row.lastError ? (
                      <p className="mt-1 max-w-xs truncate text-2xs text-ink-muted" title={row.lastError}>
                        {row.lastError}
                      </p>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.attempts}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog
        open={revealedReadToken != null}
        onOpenChange={(open) => {
          if (!open) setRevealedReadToken(null);
        }}
      >
        <DialogContent
          title="Guarde este token"
          description="Ele só lê. Quem tiver o token vê pedidos, SKUs, anúncios e reclamações."
        >
          <div className="flex flex-col gap-3">
            <code className="break-all rounded-control bg-surface-sunken px-2.5 py-2 font-mono text-2xs text-ink">
              {revealedReadToken}
            </code>
            <Button
              variant="secondary"
              onClick={() => {
                if (!revealedReadToken) return;
                void copyText(revealedReadToken).then(() => {
                  toast.add({ type: "success", title: "Token copiado" });
                });
              }}
            >
              <Copy />
              Copiar token
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={revealedSecret != null}
        onOpenChange={(open) => {
          if (!open) setRevealedSecret(null);
        }}
      >
        <DialogContent
          title="Guarde este segredo"
          description="Ele assina cada POST. Não aparece de novo — rode rotacionar se perder."
        >
          <div className="flex flex-col gap-3">
            <code className="break-all rounded-control bg-surface-sunken px-2.5 py-2 font-mono text-2xs text-ink">
              {revealedSecret}
            </code>
            <Button
              variant="secondary"
              onClick={() => {
                if (!revealedSecret) return;
                void copyText(revealedSecret).then(() => {
                  toast.add({ type: "success", title: "Segredo copiado" });
                });
              }}
            >
              <Copy />
              Copiar segredo
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

type Endpoint = {
  id: string;
  name: string;
  url: string;
  secretPrefix: string;
  events: string[];
  enabled: boolean;
};

function EndpointCard({
  endpoint,
  delay,
  onReveal,
}: {
  endpoint: Endpoint;
  delay: number;
  onReveal: (secret: string | null) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const patch = useMutation({
    mutationFn: async (body: { enabled?: boolean; events?: OutboundEvent[] }) =>
      unwrap(await api.integrations.outbound({ id: endpoint.id }).patch(body)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["outbound"] });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível atualizar",
        description: apiErrorMessage(error, "Tente de novo."),
      });
    },
  });

  const rotate = useMutation({
    mutationFn: async () =>
      unwrap(await api.integrations.outbound({ id: endpoint.id }).rotate.post()),
    onSuccess: (data) => {
      onReveal(data.endpoint.secret);
      void queryClient.invalidateQueries({ queryKey: ["outbound"] });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível rotacionar",
        description: apiErrorMessage(error, "Tente de novo."),
      });
    },
  });

  const test = useMutation({
    mutationFn: async () =>
      unwrap(await api.integrations.outbound({ id: endpoint.id }).test.post()),
    onSuccess: () => {
      toast.add({ type: "success", title: "Teste na fila" });
      void queryClient.invalidateQueries({ queryKey: ["outbound"] });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível testar",
        description: apiErrorMessage(error, "Tente de novo."),
      });
    },
  });

  const remove = useMutation({
    mutationFn: async () =>
      unwrap(await api.integrations.outbound({ id: endpoint.id }).delete()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["outbound"] });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível remover",
        description: apiErrorMessage(error, "Tente de novo."),
      });
    },
  });

  return (
    <Card
      className="animate-rise"
      style={{ animationDelay: `${delay}ms` }}
    >
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>{endpoint.name}</CardTitle>
          <p className="mt-0.5 truncate font-mono text-2xs text-ink-muted" title={endpoint.url}>
            {endpoint.url}
          </p>
        </div>
        <Switch
          checked={endpoint.enabled}
          onCheckedChange={(enabled) => patch.mutate({ enabled })}
        />
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-1.5">
          {EVENT_OPTIONS.map((row) => {
            const on = endpoint.events.includes(row.id);
            return (
              <button
                key={row.id}
                type="button"
                onClick={() => {
                  const next = on
                    ? endpoint.events.filter((event) => event !== row.id)
                    : [...endpoint.events, row.id];
                  if (next.length === 0) return;
                  patch.mutate({ events: next as OutboundEvent[] });
                }}
                className={`rounded-full px-2 py-0.5 text-2xs transition-[transform,background-color,color] duration-150 ease-swift active:scale-[0.97] ${
                  on
                    ? "bg-ember/10 text-ember"
                    : "bg-surface-sunken text-ink-muted"
                }`}
              >
                {row.title}
              </button>
            );
          })}
        </div>
        <p className="font-mono text-2xs text-ink-muted">{endpoint.secretPrefix}</p>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            loading={test.isPending}
            onClick={() => test.mutate()}
          >
            <Send />
            Enviar teste
          </Button>
          <Button
            size="sm"
            variant="secondary"
            loading={rotate.isPending}
            onClick={() => rotate.mutate()}
          >
            <RefreshCw />
            Novo segredo
          </Button>
          <Button
            size="sm"
            variant="ghost"
            loading={remove.isPending}
            onClick={() => remove.mutate()}
          >
            Remover
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ReadApiSection({
  onReveal,
}: {
  onReveal: (token: string | null) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ["read-tokens"],
    queryFn: async () => unwrap(await api.integrations["read-tokens"].get()),
  });
  const create = useMutation({
    mutationFn: async () =>
      unwrap(await api.integrations["read-tokens"].post({ name: "API" })),
    onSuccess: (token) => {
      onReveal(token.token);
      void queryClient.invalidateQueries({ queryKey: ["read-tokens"] });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível criar o token",
        description: apiErrorMessage(error, "Tente de novo."),
      });
    },
  });

  const tokens = list.data?.tokens ?? [];
  const readUrl = list.data?.readUrl;

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle>API de leitura</CardTitle>
        <Button
          size="sm"
          variant="secondary"
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          Novo token
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-2xs text-ink-muted">
          Bearer <code className="font-mono">fc_rd_…</code> em{" "}
          <code className="font-mono">{readUrl ?? "/integrations/read"}</code>
          /orders, /skus, /listings, /claims. Sem escrita.
        </p>
        {tokens.length === 0 ? (
          <p className="text-2xs text-ink-muted">
            Nenhum token ainda. Crie um para o seu sistema puxar os dados.
          </p>
        ) : (
          <ul className="divide-y divide-line rounded-control border border-line">
            {tokens.map((token) => (
              <ReadTokenRow
                key={token.id}
                token={token}
                onReveal={onReveal}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ReadTokenRow({
  token,
  onReveal,
}: {
  token: {
    id: string;
    name: string;
    tokenPrefix: string;
    enabled: boolean;
    lastUsedAt: string | Date | null;
  };
  onReveal: (value: string | null) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const patch = useMutation({
    mutationFn: async (enabled: boolean) =>
      unwrap(
        await api.integrations["read-tokens"]({ id: token.id }).patch({ enabled }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["read-tokens"] });
    },
  });
  const rotate = useMutation({
    mutationFn: async () =>
      unwrap(await api.integrations["read-tokens"]({ id: token.id }).rotate.post()),
    onSuccess: (data) => {
      onReveal(data.token.token);
      void queryClient.invalidateQueries({ queryKey: ["read-tokens"] });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível rotacionar",
        description: apiErrorMessage(error, "Tente de novo."),
      });
    },
  });
  const remove = useMutation({
    mutationFn: async () =>
      unwrap(await api.integrations["read-tokens"]({ id: token.id }).delete()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["read-tokens"] });
    },
  });

  return (
    <li className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="text-[0.8125rem] font-medium text-ink">{token.name}</p>
        <p className="truncate font-mono text-2xs text-ink-muted">
          {token.tokenPrefix}
          {token.lastUsedAt
            ? ` · usado ${formatDateTime(token.lastUsedAt)}`
            : ""}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Switch
          checked={token.enabled}
          onCheckedChange={(enabled) => patch.mutate(enabled)}
        />
        <Button
          size="sm"
          variant="secondary"
          loading={rotate.isPending}
          onClick={() => rotate.mutate()}
        >
          <RefreshCw />
          Novo token
        </Button>
        <Button
          size="sm"
          variant="ghost"
          loading={remove.isPending}
          onClick={() => remove.mutate()}
        >
          Remover
        </Button>
      </div>
    </li>
  );
}
